import { useEffect } from "react";
import { useLocation } from "react-router";

import { customToast } from "../../..";
import {
  featureFlagsEnums,
  infoMessages,
  signalRConstants,
  toastTypes,
  textStrings,
  SIGNALR_TIMINGS,
  sidebarScreenConstants,
  SIGNALR_FEATURE_DISPLAY,
} from "../../../../views/shared-screens/constants";
import { loginActions } from "../../../../redux/actions";
import { addListeners, removeListeners } from "../../../../system";

const {
  storeFeatureUpdate,
  brandFeatureUpdate,
  userUpdatedConnectionKey,
  userDeletedConnectionKey,
} = signalRConstants;

const featureActions = {
  [featureFlagsEnums.INBOX]: {
    isActive: (features) => features.includes(featureFlagsEnums.INBOX),
    label: sidebarScreenConstants.INBOX,
    // Use the callback belonging to the hook that received this event.
    onAction: (data, features, onInboxDisable) => onInboxDisable(data, features),
  },
  [featureFlagsEnums.IN_STORE]: {
    isActive: (features) => features.includes(featureFlagsEnums.IN_STORE),
    label: SIGNALR_FEATURE_DISPLAY.IN_STORE_LABEL,
  },
  [featureFlagsEnums.AI_REPLY]: {
    isActive: (features) => features.includes(featureFlagsEnums.AI_REPLY),
    label: textStrings.AI_REPLY,
  },
  [featureFlagsEnums.NPS_FIRST]: {
    isActive: (features) => features.includes(featureFlagsEnums.NPS_FIRST),
    label: SIGNALR_FEATURE_DISPLAY.NPS_FIRST_LABEL,
  },
};

// Give the user time to see why they need to sign in again.
const notifyAndLogout = (message) => {
  customToast(
    message,
    toastTypes.INFO,
    SIGNALR_TIMINGS.USER_PROFILE_TOAST_DURATION_MS
  );
  setTimeout(() => {
    loginActions.logout();
  }, SIGNALR_TIMINGS.LOGOUT_DELAY_MS);
};

const handleUserProfileUpdate = () => {
  notifyAndLogout(infoMessages.USER_PROFILE_UPDATED);
};

const handleUserProfileDeletion = () => {
  notifyAndLogout(infoMessages.USER_PROFILE_DELETED);
};

const useSignalRListeners = (
  signalRContext,
  user,
  getBrandsWithFeatureFlags,
  onInboxDisable
) => {
  const location = useLocation();

  const handleFeatureUpdate = (data) => {
    const isStoreUpdate = data?.storeId;

    data.updatedFeatures?.forEach((updatedFeature) => {
      const featureAction = featureActions[updatedFeature];

      if (featureAction) {
        const updateStore = data?.stores?.find(
          (store) => store?.storeId === isStoreUpdate
        );

        const features = isStoreUpdate
          ? updateStore?.storeFeatures || []
          : data.brandFeatures;
        const isActive = featureAction.isActive(features);

        if (user.id !== data.updatedByUserId) {
          const name = isStoreUpdate ? data.storeName : data.brandName;
          const status = isActive
            ? SIGNALR_FEATURE_DISPLAY.ENABLED
            : SIGNALR_FEATURE_DISPLAY.DISABLED;
          const message = `${featureAction.label} feature has been ${status} for ${name}!`;
          customToast(
            message,
            toastTypes.INFO,
            SIGNALR_TIMINGS.FEATURE_UPDATE_TOAST_DURATION_MS
          );
        }

        if (featureAction.onAction) {
          featureAction.onAction(data, features, onInboxDisable);
        }
      }
    });

    getBrandsWithFeatureFlags();
  };

  useEffect(() => {
    addListeners({
      connection: signalRContext?.connection,
      listeners: [
        {
          key: userUpdatedConnectionKey,
          callbackFunction: handleUserProfileUpdate,
        },
        {
          key: userDeletedConnectionKey,
          callbackFunction: handleUserProfileDeletion,
        },
        { key: brandFeatureUpdate, callbackFunction: handleFeatureUpdate },
        { key: storeFeatureUpdate, callbackFunction: handleFeatureUpdate },
      ],
    });

    return () => {
      removeListeners({
        connection: signalRContext?.connection,
        listeners: [
          storeFeatureUpdate,
          brandFeatureUpdate,
          userUpdatedConnectionKey,
          userDeletedConnectionKey,
        ],
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    signalRContext?.connection?.state,
    signalRContext?.connection?.connectionId,
    location.pathname,
  ]);
};

export default useSignalRListeners;
